import {
  BOT_TESTER_INVITE_TOKEN_LENGTH,
  MAX_INVITE_EXPIRY_DAYS,
  MAX_INVITE_MAX_USES,
  type BotTesterInviteState,
} from '../../types/botTesterInvite.js';

// SMS Lead Bot — tester invite links. PURE, shared by the public page, the
// staff list and the server, so none of the three can disagree about whether a
// link still works.
//
// See src/types/botTesterInvite.ts for why this is an invite link rather than
// the public form that was asked for.

/**
 * ⚠️ EVALUATED AT READ TIME against `nowMs` — there is no expiry cron and no
 * stored "expired" flag (the hostedReport / acknowledgement precedent). A link
 * therefore stops working at the moment it should, rather than at the moment a
 * job next runs.
 *
 * ⚠️ THE ORDER IS THE MESSAGE. Revoked beats expired beats used-up: a human
 * killed this one, so saying "it expired" would misdescribe why and imply an
 * extension would help. Used-up is last because it is the only one the TESTER
 * can cause, and it is the least alarming thing to be told.
 */
export function classifyInviteState(
  invite: {
    revokedAtMs: number | null;
    expiresAtMs: number;
    usedCount: number;
    maxUses: number;
  },
  nowMs: number,
): BotTesterInviteState {
  if (invite.revokedAtMs !== null) return 'revoked';
  if (invite.expiresAtMs <= nowMs) return 'expired';
  // ⚠️ `>=`, not `>`. maxUses is the number of numbers the link may add, so the
  // Nth use is the last one — an off-by-one here is one more stranger than
  // anyone authorised.
  if (invite.usedCount >= invite.maxUses) return 'used_up';
  return 'ok';
}

/**
 * A token is exactly 32 URL-safe alphanumerics. ⚠️ Anything else is refused
 * WITHOUT a lookup, so a scanner walking short or malformed tokens costs a
 * string comparison rather than a Firestore read.
 */
export function isValidInviteToken(token: string): boolean {
  return (
    typeof token === 'string' &&
    token.length === BOT_TESTER_INVITE_TOKEN_LENGTH &&
    /^[A-Za-z0-9]+$/.test(token)
  );
}

/**
 * What the public page says when a link does not work. ⚠️ Each state gets its
 * OWN sentence rather than one generic "this link is not valid": a tester whose
 * link ran out needs a different next step from one whose link was revoked, and
 * "ask whoever sent it" is only useful advice if they know what happened.
 */
export function describeInviteState(state: Exclude<BotTesterInviteState, 'ok'> | 'not_found'): {
  title: string;
  detail: string;
} {
  switch (state) {
    case 'revoked':
      return {
        title: 'This link has been turned off',
        detail: 'Whoever sent it to you can issue a new one.',
      };
    case 'expired':
      return {
        title: 'This link has expired',
        detail: 'Test links only last a short while. Ask for a fresh one.',
      };
    case 'used_up':
      return {
        title: 'This link has been used up',
        detail:
          'It was set up for a limited number of testers and they have all signed up. ' +
          'Ask for your own link.',
      };
    case 'not_found':
    default:
      return {
        title: 'We could not find that link',
        detail: 'Check you copied the whole address, or ask for a new link.',
      };
  }
}

export type InviteSettingsError = string | null;

/**
 * ⚠️ ONE VALIDATOR for the create form and the API, the `capError` posture — a
 * form that accepts a value the API rejects teaches people to distrust the save
 * button, and here it would do it while they are trying to give someone access.
 */
export function inviteMaxUsesError(value: unknown, ceiling: number = MAX_INVITE_MAX_USES): InviteSettingsError {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return `How many testers needs a number between 1 and ${ceiling}.`;
  }
  if (!Number.isInteger(value)) return 'How many testers must be a whole number.';
  if (value < 1) {
    // ⚠️ Same shape of rule as the runaway caps: there is no unlimited, and the
    // message says why rather than restating the bound.
    return (
      'A link has to be good for at least one tester. There is no unlimited option — ' +
      'a link that can add any number of phones is a public sign-up form with extra steps.'
    );
  }
  if (value > ceiling) {
    return `A single link cannot be good for more than ${ceiling} testers.`;
  }
  return null;
}

export function inviteExpiryDaysError(value: unknown, ceiling: number = MAX_INVITE_EXPIRY_DAYS): InviteSettingsError {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return `Expires in needs a number of days between 1 and ${ceiling}.`;
  }
  if (!Number.isInteger(value)) return 'Expires in must be a whole number of days.';
  if (value < 1) return 'A link has to last at least a day.';
  if (value > ceiling) {
    return `A link cannot last more than ${ceiling} days. The trial has an end.`;
  }
  return null;
}

/** The tester-facing URL for a token. One definition, so the copy button and
 *  the email an AM writes by hand cannot point at different places. */
export function testerInvitePath(token: string): string {
  return `/bot-test/${token}`;
}
